<template>
  <div class="phone-number-list-container">
    <!-- 桌面端表格视图 -->
    <div v-if="!isMobile" class="bottom-section">
      <div class="card">
        <div class="card-header">
          <h3>
            {{ $t('phoneList.title') }}
            <div class="balance-tooltip tooltip-bottom">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="help-icon">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="m9,9a3,3 0 1,1 6,0c0,2 -3,3 -3,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="m12,17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div class="tooltip-content">
                <p class="heading">{{ $t('phoneList.usageTipTitle') }}</p>
                <p>{{ $t('phoneList.usageTips[0]') }}</p>
                <p>{{ $t('phoneList.usageTips[1]') }}</p>
                <p>{{ $t('phoneList.usageTips[2]') }}</p>
                <p>{{ $t('phoneList.usageTips[3]') }}</p>
              </div>
            </div>
          </h3>
          <div class="card-actions">
            <div class="auto-save-data-control">
              <label class="auto-save-data-toggle">
                <input
                  type="checkbox"
                  :checked="autoSaveData"
                  @change="$emit('update:auto-save-data', $event.target.checked)"
                />
                <span>{{ $t('phoneList.actions.autoSaveData') }}</span>
              </label>
              <div class="balance-tooltip tooltip-bottom">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="help-icon">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="m9,9a3,3 0 1,1 6,0c0,2 -3,3 -3,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="m12,17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div class="tooltip-content">
                  <p>{{ $t('phoneList.actions.autoSaveDataHint') }}</p>
                </div>
              </div>
            </div>
            <button 
              class="btn btn-outline" 
              @click="$emit('release-all')"
              :disabled="phoneList.length === 0"
            >
              {{ $t('phoneList.actions.releaseAll') }}
            </button>
            <button 
              class="btn btn-outline" 
              @click="$emit('copy-all')"
              :disabled="phoneList.length === 0"
            >
              {{ $t('phoneList.actions.copyAllNumbers') }}
            </button>
            <button 
              class="btn btn-outline audio-toggle-btn" 
              @click="$emit('toggle-audio')"
              :class="{ 'active': enableAudio }"
              :title="enableAudio ? $t('phoneList.audio.disable') : $t('phoneList.audio.enable')"
            >
              <svg v-if="enableAudio" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19.07 4.93C20.97 6.83 22 9.34 22 12C22 14.66 20.97 17.17 19.07 19.07M15.54 8.46C16.48 9.4 17 10.64 17 12C17 13.36 16.48 14.6 15.54 15.54" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M23 9L17 15M17 9L23 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ enableAudio ? $t('phoneList.audio.on') : $t('phoneList.audio.off') }}
            </button>
          </div>
        </div>
        
        <div class="card-content">
          <div v-if="phoneList.length > 0" class="table-container">
            <div class="table">
              <div class="table-header">
                <div class="table-row">
                  <div class="table-cell">#</div>
                  <div class="table-cell">{{ $t('phoneList.headers.location') }}</div>
                  <div class="table-cell">{{ $t('phoneList.headers.phoneNumber') }}</div>
                  <div class="table-cell">{{ $t('phoneList.headers.verificationCode') }}</div>
                  <div class="table-cell">{{ $t('phoneList.headers.smsContent') }}</div>
                  <div class="table-cell">{{ $t('phoneList.headers.chargeAmount') }}</div>
                  <div class="table-cell">
                    {{ $t('phoneList.headers.lastGetTime') }}
                    <div class="balance-tooltip tooltip-bottom">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="help-icon">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                        <path d="m9,9a3,3 0 1,1 6,0c0,2 -3,3 -3,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="m12,17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <div class="tooltip-content">
                        <p class="heading">{{ $t('phoneList.lastGetTimeTestTitle') }}</p>
                        <p>{{ $t('phoneList.lastGetTimeTestTip') }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="table-cell">{{ $t('phoneList.headers.status') }}</div>
                  <div class="table-cell actions-cell">{{ $t('phoneList.headers.actions') }}</div>
                </div>
              </div>
              <div class="table-body">
                <div v-for="(phone, index) in phoneList" :key="phone.id" class="table-row">
                  <div class="table-cell">{{ index + 1 }}</div>
                  <div class="table-cell">{{ phone.location }}</div>
                  <div class="table-cell phone-number" @click="copyText(phone.phoneNumber)">
                    {{ phone.phoneNumber }}
                  </div>
                  <div class="table-cell">
                    <span v-if="phone.verificationCode" class="verification-code" @click="copyText(phone.verificationCode)">
                      {{ phone.verificationCode }}
                    </span>
                    <span v-else class="no-code">{{ $t('phoneList.status.waiting') }}</span>
                  </div>
                  <div class="table-cell message-content" @click="copyText(phone.smsContent || '')">
                    {{ phone.smsContent || $t('phoneList.status.waitingSms') }}
                  </div>
                  <div class="table-cell">
                    <div class="charge-info">
                      <span class="current-price">{{ getChargeAmount(phone) }}</span>
                      <span v-if="hasActivity(phone)" class="original-price">{{ getOriginalAmount(phone) }}</span>
                    </div>
                  </div>
                  <div class="table-cell">
                    {{ phone.lastGetTime ? formatTime(phone.lastGetTime) : '--' }}
                  </div>
                  <div class="table-cell">
                    <span class="status-badge" :class="phone.status">
                      {{ getStatusText(phone.status) }} ({{ phone.remainingTries }})
                    </span>
                  </div>
                  <div class="table-cell actions-cell">
                    <button 
                      class="action-btn release-btn" 
                      @click="$emit('release-phone', phone.id)"
                      :disabled="isReleaseLoading(phone.id) || isBlacklistLoading(phone.id)"
                    >
                      <span v-if="isReleaseLoading(phone.id)" class="btn-spinner"></span>
                      {{ $t('phoneList.actions.release') }}
                    </button>
                    <button 
                      class="action-btn blacklist-btn" 
                      @click="$emit('add-blacklist', phone)"
                      :disabled="isReleaseLoading(phone.id) || isBlacklistLoading(phone.id)"
                    >
                      <span v-if="isBlacklistLoading(phone.id)" class="btn-spinner"></span>
                      {{ $t('phoneList.actions.blacklist') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 5h18M3 12h18M3 19h18" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p>{{ $t('phoneList.noPhones') }}</p>
            <p class="empty-state-hint">{{ $t('phoneList.getPhoneHint') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端列表视图 -->
    <div v-else class="mobile-message-list">
      <div class="mobile-list-header">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" stroke-width="2"/>
          <path d="M3 10h18" stroke="currentColor" stroke-width="2"/>
        </svg>
        {{ $t('messageProcessing.messageList') }}
        <div class="mobile-header-actions">
          <label class="mobile-auto-save-data-toggle">
            <input
              type="checkbox"
              :checked="autoSaveData"
              @change="$emit('update:auto-save-data', $event.target.checked)"
            />
            <span>{{ $t('phoneList.actions.autoSaveData') }}</span>
          </label>
          <button 
            class="mobile-icon-btn" 
            @click="$emit('release-all')"
            :disabled="phoneList.length === 0"
            :title="$t('phoneList.actions.releaseAll')"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button 
            class="mobile-icon-btn" 
            @click="$emit('copy-all')"
            :disabled="phoneList.length === 0"
            :title="$t('phoneList.actions.copyAllNumbers')"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="2"/>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
          <button 
            class="mobile-icon-btn" 
            :class="{ 'active': enableAudio }" 
            @click="$emit('toggle-audio')"
            :title="enableAudio ? $t('phoneList.audio.disable') : $t('phoneList.audio.enable')"
          >
            <svg v-if="enableAudio" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19.07 4.93C20.97 6.83 22 9.34 22 12C22 14.66 20.97 17.17 19.07 19.07M15.54 8.46C16.48 9.4 17 10.64 17 12C17 13.36 16.48 14.6 15.54 15.54" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M23 9L17 15M17 9L23 15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div v-for="phone in phoneList" :key="phone.id" class="mobile-message-item">
        <div class="mobile-message-header-row">
          <div class="message-number">
            <span class="message-label">{{ $t('mobile.phoneInfo.phoneNumber') }}</span>
            <span class="message-value phone-number" @click="copyText(phone.phoneNumber)">
              {{ phone.phoneNumber }}
            </span>
          </div>
          <div class="message-status">
            <span class="mobile-status-badge" :class="phone.status">
              {{ getStatusText(phone.status) }}
            </span>
            <span class="remaining-count">({{ phone.remainingTries }})</span>
          </div>
        </div>
        
        <div class="mobile-message-content" v-if="phone.verificationCode || phone.smsContent">
          <div class="verification-code-row" v-if="phone.verificationCode">
            <span class="message-label">{{ $t('mobile.phoneInfo.verificationCode') }}</span>
            <span class="verification-code mobile-code" @click="copyText(phone.verificationCode)">
              {{ phone.verificationCode }}
            </span>
          </div>
          <div class="sms-content-row" v-if="phone.smsContent">
            <span class="message-label">{{ $t('mobile.phoneInfo.smsContent') }}</span>
            <span class="sms-content" @click="copyText(phone.smsContent)">
              {{ phone.smsContent }}
            </span>
          </div>
          <div class="last-get-time-row" v-if="phone.lastGetTime">
            <span class="message-label">{{ $t('mobile.phoneInfo.lastGetTime') }}</span>
            <span class="last-get-time">{{ formatTime(phone.lastGetTime) }}</span>
          </div>
        </div>
        
        <div class="mobile-charge-info">
          <span class="message-label">{{ $t('phoneList.headers.chargeAmount') }}</span>
          <div class="charge-info">
            <span class="current-price">{{ getChargeAmount(phone) }}</span>
            <span v-if="hasActivity(phone)" class="original-price">{{ getOriginalAmount(phone) }}</span>
          </div>
        </div>

        <div class="mobile-message-meta">
          <span class="message-time">{{ formatTime(phone.createTime) }}</span>
          <div class="mobile-message-actions">
            <button 
              class="mobile-control-btn" 
              @click="$emit('release-phone', phone.id)"
              :disabled="isReleaseLoading(phone.id)"
            >
              <span v-if="isReleaseLoading(phone.id)" class="btn-spinner"></span>
              {{ $t('mobile.phoneInfo.release') }}
            </button>
            <button 
              class="mobile-control-btn danger" 
              @click="$emit('add-blacklist', phone)"
              :disabled="isBlacklistLoading(phone.id)"
            >
              <span v-if="isBlacklistLoading(phone.id)" class="btn-spinner"></span>
              {{ $t('phoneList.actions.blacklist') }}
            </button>
          </div>
        </div>
      </div>
      
      <div v-if="phoneList.length === 0" class="mobile-empty-message">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="5" y="2" width="14" height="20" rx="2" stroke-width="2"/>
          <path d="M12 18h.01" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <p>{{ $t('phoneList.noPhones') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'

export default {
  name: 'PhoneNumberList',
  props: {
    phoneList: {
      type: Array,
      default: () => []
    },
    releaseLoading: {
      type: Set,
      default: () => new Set()
    },
    blacklistLoading: {
      type: Set,
      default: () => new Set()
    },
    enableAudio: {
      type: Boolean,
      default: false
    },
    autoSaveData: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    isReleaseLoading(phoneId) {
      return this.releaseLoading.has(phoneId);
    },
    isBlacklistLoading(phoneId) {
      return this.blacklistLoading.has(phoneId);
    },
    getStatusText(status) {
      const statusMap = {
        'waiting': this.$t('phoneList.status.waiting'),
        'received': this.$t('phoneList.status.received'),
        'timeout': this.$t('phoneList.status.timeout')
      };
      return statusMap[status] || this.$t('common.unknown');
    },
    hasValue(value) {
      return value !== undefined && value !== null && value !== '';
    },
    hasActivity(row) {
      const discountAmount = Number(row?.discountAmount);
      const hasPositiveDiscount = !Number.isNaN(discountAmount) && discountAmount > 0;
      const hasDiscountPrice = this.hasValue(row?.discountUserMoney) && this.hasValue(row?.originalUserMoney) && String(row.discountUserMoney) !== String(row.originalUserMoney);
      return Boolean(row?.discountActivityId || hasPositiveDiscount || hasDiscountPrice);
    },
    getChargeAmount(row) {
      return this.hasValue(row?.userMoney) ? row.userMoney : (this.hasValue(row?.discountUserMoney) ? row.discountUserMoney : '--');
    },
    getOriginalAmount(row) {
      return this.hasValue(row?.originalUserMoney) ? row.originalUserMoney : this.getChargeAmount(row);
    },
    formatTime(time) {
      if (!time) return '--';
      const date = new Date(time);
      return date.toLocaleString('zh-CN');
    },
    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        ElMessage.success(this.$t('messages.copySuccess'));
      } catch (err) {
        ElMessage.error(this.$t('messages.copyError'));
      }
    }
  }
}
</script>

<style scoped>
/* 表格样式 */
.table-container {
  overflow-x: auto;
}

.table {
  width: 100%;
  min-width: 940px;
}

.table-header {
  background: #F9FAFB;
  border-bottom: 2px solid #E5E7EB;
}

.table-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #E5E7EB;
  transition: background 0.2s ease;
}

.table-body .table-row:hover {
  background: #F9FAFB;
}

.table-cell {
  flex: 1;
  padding: 0 8px;
  font-size: 14px;
  color: #374151;
}

.table-cell:first-child {
  flex: 0 0 50px;
}

.table-cell.actions-cell {
  flex: 0 0 180px;
  display: flex;
  gap: 8px;
}

.charge-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.current-price {
  color: #FF2D2D;
  font-weight: 800;
  text-shadow: 0 0 10px rgba(255, 45, 45, 0.22);
}

.original-price {
  color: #9CA3AF;
  font-size: 12px;
  font-weight: 500;
  text-decoration: line-through;
}

.table-header .table-cell {
  font-weight: 600;
  color: #111827;
}

.phone-number {
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  color: var(--mobile-primary);
  transition: all 0.2s ease;
}

.phone-number:hover {
  background: rgba(68, 97, 242, 0.1);
}

.verification-code {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: var(--primary-color);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(68, 97, 242, 0.1);
  transition: all 0.2s ease;
}

.verification-code:hover {
  background: rgba(68, 97, 242, 0.2);
}

.no-code {
  color: #9CA3AF;
  font-style: italic;
}

.message-content {
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.waiting {
  background: #FEF3C7;
  color: #92400E;
}

.status-badge.received {
  background: #D1FAE5;
  color: #065F46;
}

.status-badge.timeout {
  background: #FEE2E2;
  color: #991B1B;
}

.action-btn {
  padding: 4px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  background: #F9FAFB;
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.release-btn:hover:not(:disabled) {
  background: #FEF3C7;
  border-color: #F59E0B;
  color: #92400E;
}

.blacklist-btn:hover:not(:disabled) {
  background: #FEE2E2;
  border-color: #EF4444;
  color: #991B1B;
}

/* 移动端消息列表 */
.mobile-list-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--mobile-card-bg);
  border-radius: var(--mobile-radius) var(--mobile-radius) 0 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--mobile-text-primary);
}

.mobile-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.auto-save-data-toggle,
.mobile-auto-save-data-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.auto-save-data-control {
  display: flex;
  align-items: center;
}

.auto-save-data-control .balance-tooltip {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
}

.auto-save-data-toggle input,
.mobile-auto-save-data-toggle input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--primary-color);
  cursor: pointer;
}

.mobile-auto-save-data-toggle {
  color: var(--mobile-text-primary);
  font-size: 12px;
}

.mobile-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 122, 255, 0.1);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
}

.mobile-icon-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.mobile-icon-btn.active {
  background: var(--mobile-primary);
  color: white;
}

.mobile-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-message-item {
  background: var(--mobile-card-bg);
  border-radius: var(--mobile-radius);
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-left: 4px solid var(--mobile-primary);
}

.mobile-message-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.message-number {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-label {
  font-size: 12px;
  color: var(--mobile-text-secondary);
}

.message-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--mobile-text-primary);
}

.message-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.mobile-status-badge.waiting {
  background: rgba(251, 191, 36, 0.1);
  color: var(--mobile-warning);
}

.mobile-status-badge.received {
  background: rgba(52, 211, 153, 0.1);
  color: var(--mobile-success);
}

.mobile-status-badge.timeout {
  background: rgba(255, 59, 48, 0.1);
  color: var(--mobile-error);
}

.remaining-count {
  font-size: 12px;
  color: var(--mobile-text-secondary);
}

.mobile-message-content {
  padding: 12px 0;
  border-top: 1px solid var(--mobile-border);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.verification-code-row,
.sms-content-row,
.last-get-time-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-code {
  font-size: 20px;
  font-weight: 700;
  color: var(--mobile-primary);
}

.sms-content {
  font-size: 14px;
  color: var(--mobile-text-primary);
  line-height: 1.5;
}

.last-get-time {
  font-size: 12px;
  color: var(--mobile-text-secondary);
}

.mobile-charge-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--mobile-border);
}

.mobile-charge-info .charge-info {
  align-items: flex-end;
}

.mobile-message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--mobile-border);
}

.message-time {
  font-size: 12px;
  color: var(--mobile-text-secondary);
}

.mobile-message-actions {
  display: flex;
  gap: 8px;
}

.mobile-control-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--mobile-border);
  background: white;
  color: var(--mobile-text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-control-btn:active {
  transform: scale(0.95);
}

.mobile-control-btn.danger {
  background: rgba(255, 59, 48, 0.1);
  color: var(--mobile-error);
}

.mobile-empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
  color: var(--mobile-text-secondary);
}

.mobile-empty-message svg {
  opacity: 0.3;
  margin-bottom: 16px;
}

.mobile-empty-message p {
  font-size: 14px;
  margin: 0;
}

/* 移动端响应式 */
@media (max-width: 768px) {
  .phone-number {
    background: rgba(68, 97, 242, 0.1);
  }
}

/* 仅在移动端显示背景色 */
@media (max-width: 768px) {
  .phone-number {
    background: rgba(68, 97, 242, 0.1);
  }
}


/* 加载动画 */
.btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #E5E7EB;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 桌面端音频切换按钮 */
.audio-toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
}

.audio-toggle-btn.active {
  background: rgba(68, 97, 242, 0.1);
  color: var(--primary-color);
  border-color: var(--primary-color);
}

</style>
